import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  buildNativeAppDeepLink,
  isNativeApp,
  nativeOpenUrlToAppPath,
} from '../lib/nativeAppScheme';
import { deepLinkNavigate } from '../lib/deepLinkNavigation';

/**
 * React fallback for /oauth-app-return (prefer public/oauth-app-return.html).
 * Native / mobile: try custom-scheme handoff.
 * Desktop web: continue in this browser into the SPA with OAuth params intact.
 */
export default function OauthAppReturn() {
  const navigate = useNavigate();
  const [showButton, setShowButton] = useState(false);

  const appPath = useMemo(() => {
    const fromUrl = nativeOpenUrlToAppPath(window.location.href);
    if (fromUrl) return fromUrl;
    const params = new URLSearchParams(window.location.search);
    params.delete('next');
    if (!params.has('gbp_oauth')) params.set('gbp_oauth', '1');
    if (!params.has('tab')) params.set('tab', 'integrations');
    return `/app-store?${params.toString()}`;
  }, []);

  const deepLink = useMemo(() => buildNativeAppDeepLink(appPath), [appPath]);
  const isMobile = useMemo(
    () => /iPhone|iPad|iPod|Android/i.test(navigator.userAgent || ''),
    [],
  );

  useEffect(() => {
    if (isNativeApp()) {
      deepLinkNavigate(appPath);
      navigate(appPath, { replace: true });
      return;
    }

    // Desktop browser: never bounce to com.xdetailstudio… — stay in the SPA.
    if (!isMobile) {
      navigate(appPath, { replace: true });
      return;
    }

    window.location.replace(deepLink);
    const t1 = window.setTimeout(() => setShowButton(true), 800);
    const t2 = window.setTimeout(() => {
      navigate(appPath, { replace: true });
    }, 2200);
    return () => {
      window.clearTimeout(t1);
      window.clearTimeout(t2);
    };
  }, [appPath, deepLink, isMobile, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-6">
      <div className="max-w-sm w-full text-center space-y-4">
        <div className="mx-auto h-10 w-10 animate-spin rounded-full border-2 border-blue-600 border-t-transparent" />
        <div>
          <h1 className="text-lg font-semibold text-gray-900">Opening Nexus…</h1>
          <p className="text-sm text-gray-500 mt-1">
            Finishing Google Business connection and returning you to the app.
          </p>
        </div>
        {showButton ? (
          <div className="space-y-2">
            <button
              type="button"
              onClick={() => {
                window.location.href = deepLink;
              }}
              className="w-full rounded-xl bg-blue-600 text-white text-sm font-semibold px-4 py-3 hover:bg-blue-700"
            >
              Open Nexus app
            </button>
            <button
              type="button"
              onClick={() => navigate(appPath, { replace: true })}
              className="w-full rounded-xl bg-slate-200 text-slate-900 text-sm font-semibold px-4 py-3 hover:bg-slate-300"
            >
              Continue in browser
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
}
